import { useState, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useTheme } from '../context/ThemeContext'
import { useNotification } from '../context/NotificationContext'
import { Button } from '../design-system'
import { HiEye, HiEyeOff, HiClipboardCopy } from 'react-icons/hi'
import { QRCodeSVG } from 'qrcode.react'
import logoIcon from '../assets/streamline-logo-fullcolour.svg'
import logoText from '../assets/streamline-logo-fullcolour-textonly.svg'
import './Login.css'

const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{8,}$/

const STATES = {
  LOGIN: 'LOGIN',
  CONFIRM_MFA: 'CONFIRM_MFA',
  SETUP_MFA: 'SETUP_MFA',
  FORGOT_PASSWORD: 'FORGOT_PASSWORD',
  FORCE_RESET: 'FORCE_RESET_PASSWORD',
  VERIFY_EMAIL: 'VERIFY_EMAIL',
}

export default function Login() {
  const navigate = useNavigate()
  const auth = useAuth()
  const toast = useNotification()

  const [loginState, setLoginState] = useState(STATES.LOGIN)
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [qrCode, setQrCode] = useState(null)
  const [forgotSent, setForgotSent] = useState(false)
  const [verificationSent, setVerificationSent] = useState(false)

  const [loginForm, setLoginForm] = useState({ email: '', password: '' })
  const [totpCode, setTotpCode] = useState('')
  const [newPasswordForm, setNewPasswordForm] = useState({ newPassword: '', confirmNewPassword: '' })
  const [forgotEmail, setForgotEmail] = useState('')
  const [forgotForm, setForgotForm] = useState({ code: '', newPassword: '', confirmNewPassword: '' })
  const [verifyCode, setVerifyCode] = useState('')

  const clearForms = useCallback(() => {
    setLoginForm({ email: '', password: '' })
    setTotpCode('')
    setNewPasswordForm({ newPassword: '', confirmNewPassword: '' })
    setForgotEmail('')
    setForgotForm({ code: '', newPassword: '', confirmNewPassword: '' })
    setVerifyCode('')
    setForgotSent(false)
    setVerificationSent(false)
  }, [])

  const { theme } = useTheme?.() || { theme: 'dark' }

  const handleError = useCallback((err) => {
    const code = err?.code || ''
    const messages = {
      NotAuthorizedException: 'Invalid user credentials',
      UserNotFoundException: 'Invalid user credentials',
      UserNotConfirmedException: 'Account not confirmed. Please contact an administrator.',
      PasswordResetRequiredException: "Account password has been force-reset. Click 'Forgot Password' to set a new password.",
      CodeMismatchException: 'Invalid code',
    }
    if (code === 'NotAuthorizedException' || code === 'UserNotFoundException') {
      auth.signOut?.()
    }
    toast.error(messages[code] || err?.message || 'An error occurred')
  }, [toast, auth])

  const runAuth = useCallback(async (fn) => {
    setIsLoading(true)
    try {
      await fn()
    } catch (err) {
      handleError(err)
    } finally {
      setIsLoading(false)
    }
  }, [handleError])

  const continueFromChallenge = useCallback(async (result) => {
    if (result?.challenge === 'SOFTWARE_TOKEN_MFA') {
      setLoginState(STATES.CONFIRM_MFA)
      return true
    }
    if (result?.challenge === 'MFA_SETUP') {
      const secretCode = await auth.beginSetupTOTP()
      setQrCode(secretCode)
      setLoginState(STATES.SETUP_MFA)
      return true
    }
    if (result?.challenge === 'NEW_PASSWORD_REQUIRED') {
      setLoginState(STATES.FORCE_RESET)
      return true
    }
    return false
  }, [auth])

  const handleLogin = () => runAuth(async () => {
    if (!loginForm.email || !loginForm.password) return
    const result = await auth.signIn(loginForm.email, loginForm.password)
    await continueFromChallenge(result)
  })

  const handleConfirmTotp = () => runAuth(async () => {
    if (!totpCode) return
    if (loginState === STATES.SETUP_MFA) {
      await auth.confirmSetupTOTP(totpCode)
    } else {
      await auth.confirmSignIn(totpCode)
    }
    setQrCode(null)
    navigate('/overview')
  })

  const handleForgotSend = () => runAuth(async () => {
    if (!forgotEmail) return
    await auth.sendForgotPasswordCode(forgotEmail)
    setForgotSent(true)
  })

  const handleForgotConfirm = () => runAuth(async () => {
    if (!forgotForm.code || !forgotForm.newPassword) return
    if (forgotForm.newPassword !== forgotForm.confirmNewPassword) {
      toast.error('Passwords do not match')
      return
    }
    await auth.verifyForgotPasswordCode(forgotEmail, forgotForm.code, forgotForm.newPassword)
    clearForms()
    setLoginState(STATES.LOGIN)
    toast.success('Password reset successfully')
  })

  const handleForceReset = () => runAuth(async () => {
    if (!newPasswordForm.newPassword) return
    if (newPasswordForm.newPassword !== newPasswordForm.confirmNewPassword) {
      toast.error('Passwords do not match')
      return
    }
    const result = await auth.forcePasswordReset(newPasswordForm.newPassword)
    const stayedOnLogin = await continueFromChallenge(result)
    if (!stayedOnLogin) navigate('/overview')
  })

  const handleSendVerification = () => runAuth(async () => {
    await auth.sendEmailVerification()
    setVerificationSent(true)
  })

  const handleConfirmVerification = () => runAuth(async () => {
    if (!verifyCode) return
    await auth.confirmEmailVerification(verifyCode)
    navigate('/overview')
  })

  const cancel = () => {
    clearForms()
    setQrCode(null)
    setLoginState(STATES.LOGIN)
  }

  const handleKeyDown = (e, handler) => {
    if (e.key === 'Enter') handler()
  }

  return (
    <div className="login-page">
      {loginState === STATES.LOGIN && (
        <div className="login-card">
          <div className="card-accent" />
          <div className="login-logo">
            <img src={logoIcon} alt="" className="login-logo-icon" aria-hidden="true" />
            <img src={logoText} alt="Streamline" className="login-logo-text" />
          </div>
          <div className="login-form" onKeyDown={(e) => handleKeyDown(e, handleLogin)}>
            <label htmlFor="login-email">Email</label>
            <input
              id="login-email"
              className="login-input"
              type="email"
              placeholder="Enter your email"
              autoComplete="email"
              value={loginForm.email}
              onChange={(e) => setLoginForm(f => ({ ...f, email: e.target.value }))}
            />

            <label htmlFor="login-password">Password</label>
            <div className="input-with-toggle">
              <input
                id="login-password"
                className="login-input"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                autoComplete="current-password"
                value={loginForm.password}
                onChange={(e) => setLoginForm(f => ({ ...f, password: e.target.value }))}
              />
              <button type="button" className="show-hide-btn" onClick={() => setShowPassword(p => !p)} tabIndex={-1} title={showPassword ? 'Hide password' : 'Show password'}>
                {showPassword ? <HiEyeOff size={16} /> : <HiEye size={16} />}
              </button>
            </div>

            <div className="login-submit">
              <Button
                variant="primary"
                disabled={!loginForm.email || !loginForm.password || isLoading}
                onClick={handleLogin}
              >
                {isLoading ? 'Signing in...' : 'Log In'}
              </Button>
            </div>
          </div>
          <div className="forgot-link">
            <Button variant="text" disabled={isLoading} onClick={() => { clearForms(); setLoginState(STATES.FORGOT_PASSWORD) }}>
              Forgot Password?
            </Button>
          </div>
        </div>
      )}

      {(loginState === STATES.SETUP_MFA || loginState === STATES.CONFIRM_MFA) && (
        <div className={`login-card ${loginState === STATES.SETUP_MFA ? 'wide' : ''}`}>
          <div className="card-accent" />
          <div className="login-logo">
            <img src={logoIcon} alt="" className="login-logo-icon" aria-hidden="true" />
            <img src={logoText} alt="Streamline" className="login-logo-text" />
          </div>

          {loginState === STATES.SETUP_MFA && (
            <ol className="mfa-instructions">
              <li>
                Install an external Authenticator app such as{' '}
                <a href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2" target="_blank" rel="noopener noreferrer">
                  Google Authenticator
                </a>
                {' '}for mobile or{' '}
                <a href="https://apps.microsoft.com/store/detail/winotp-authenticator/9NF2RGQKX1MV" target="_blank" rel="noopener noreferrer">
                  WinOTP Authenticator
                </a>
                {' '}for PC.
              </li>
              <li>Scan the QR code below or manually enter the key in your authenticator.</li>
              <li>Input the code from the authenticator app to confirm MFA setup.</li>
            </ol>
          )}

          <div className="login-form" onKeyDown={(e) => handleKeyDown(e, handleConfirmTotp)}>
            {qrCode && (
              <>
                <div className="mfa-qr-container">
                  <QRCodeSVG
                    value={`otpauth://totp/Streamline:${loginForm.email}?secret=${qrCode}&issuer=Streamline`}
                    size={180}
                    bgColor="transparent"
                    fgColor={theme === 'dark' ? '#e8e9eb' : '#111111'}
                    level="M"
                  />
                </div>
                <div className="code-copy-row">
                  <input className="login-input" readOnly value={qrCode} />
                  <button className="copy-button" onClick={() => { navigator.clipboard.writeText(qrCode); toast.success('Copied!') }} title="Copy to clipboard">
                    <HiClipboardCopy size={16} />
                  </button>
                </div>
              </>
            )}

            <label>Code</label>
            <input
              className="login-input"
              placeholder="Enter MFA Code..."
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value)}
              autoFocus
            />
            <div className="login-submit">
              <Button variant="primary" disabled={!totpCode || isLoading} onClick={handleConfirmTotp}>
                {isLoading ? 'Verifying...' : loginState === STATES.SETUP_MFA ? 'Setup MFA' : 'Confirm'}
              </Button>
            </div>
          </div>
          <div className="forgot-link">
            <Button variant="text" disabled={isLoading} onClick={cancel}>Return</Button>
          </div>
        </div>
      )}

      {loginState === STATES.FORCE_RESET && (
        <div className="login-card">
          <div className="card-accent" />
          <div className="login-logo">
            <img src={logoIcon} alt="" className="login-logo-icon" aria-hidden="true" />
            <img src={logoText} alt="Streamline" className="login-logo-text" />
          </div>
          <div className="login-form" onKeyDown={(e) => handleKeyDown(e, handleForceReset)}>
            <label>New Password</label>
            <div className="input-with-toggle">
              <input
                className="login-input"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter new password"
                value={newPasswordForm.newPassword}
                onChange={(e) => setNewPasswordForm(f => ({ ...f, newPassword: e.target.value }))}
              />
              <button type="button" className="show-hide-btn" onClick={() => setShowPassword(p => !p)} tabIndex={-1} title={showPassword ? 'Hide password' : 'Show password'}>
                {showPassword ? <HiEyeOff size={16} /> : <HiEye size={16} />}
              </button>
            </div>
            <p className="validation-hint">8+ characters, uppercase, lowercase, number, and special character required</p>

            <label>Confirm Password</label>
            <div className="input-with-toggle">
              <input
                className="login-input"
                type={showConfirm ? 'text' : 'password'}
                placeholder="Confirm new password"
                value={newPasswordForm.confirmNewPassword}
                onChange={(e) => setNewPasswordForm(f => ({ ...f, confirmNewPassword: e.target.value }))}
              />
              <button type="button" className="show-hide-btn" onClick={() => setShowConfirm(p => !p)} tabIndex={-1} title={showConfirm ? 'Hide password' : 'Show password'}>
                {showConfirm ? <HiEyeOff size={16} /> : <HiEye size={16} />}
              </button>
            </div>

            <div className="login-submit">
              <Button variant="primary" disabled={!newPasswordForm.newPassword || !newPasswordForm.confirmNewPassword || !PASSWORD_REGEX.test(newPasswordForm.newPassword) || isLoading} onClick={handleForceReset}>
                {isLoading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </div>
          </div>
          <div className="forgot-link">
            <Button variant="text" disabled={isLoading} onClick={cancel}>Return</Button>
          </div>
        </div>
      )}

      {loginState === STATES.FORGOT_PASSWORD && (
        <div className="login-card">
          <div className="card-accent" />
          <div className="login-logo">
            <img src={logoIcon} alt="" className="login-logo-icon" aria-hidden="true" />
            <img src={logoText} alt="Streamline" className="login-logo-text" />
          </div>
          <div className="login-form">
            {!forgotSent ? (
              <>
                <label>Email</label>
                <input
                  className="login-input"
                  type="email"
                  placeholder="Enter your email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, handleForgotSend)}
                  autoFocus
                />
                <div className="login-submit">
                  <Button variant="primary" disabled={!forgotEmail || isLoading} onClick={handleForgotSend}>
                    {isLoading ? 'Sending...' : 'Send Reset Code'}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <label>Verification Code</label>
                <input
                  className="login-input"
                  placeholder="Enter code from email"
                  value={forgotForm.code}
                  onChange={(e) => setForgotForm(f => ({ ...f, code: e.target.value }))}
                  autoFocus
                />
                <label>New Password</label>
                <input
                  className="login-input"
                  type="password"
                  placeholder="Enter new password"
                  value={forgotForm.newPassword}
                  onChange={(e) => setForgotForm(f => ({ ...f, newPassword: e.target.value }))}
                />
                <p className="validation-hint">8+ characters, uppercase, lowercase, number, and special character required</p>
                <label>Confirm Password</label>
                <input
                  className="login-input"
                  type="password"
                  placeholder="Confirm new password"
                  value={forgotForm.confirmNewPassword}
                  onChange={(e) => setForgotForm(f => ({ ...f, confirmNewPassword: e.target.value }))}
                  onKeyDown={(e) => handleKeyDown(e, handleForgotConfirm)}
                />
                <div className="login-submit">
                  <Button variant="primary" disabled={!forgotForm.code || !forgotForm.newPassword || !PASSWORD_REGEX.test(forgotForm.newPassword) || isLoading} onClick={handleForgotConfirm}>
                    {isLoading ? 'Resetting...' : 'Reset Password'}
                  </Button>
                </div>
              </>
            )}
          </div>
          <div className="forgot-link">
            <Button variant="text" disabled={isLoading} onClick={cancel}>Return to Login</Button>
          </div>
        </div>
      )}

      {loginState === STATES.VERIFY_EMAIL && (
        <div className="login-card">
          <div className="card-accent" />
          <div className="login-logo">
            <img src={logoIcon} alt="" className="login-logo-icon" aria-hidden="true" />
            <img src={logoText} alt="Streamline" className="login-logo-text" />
          </div>
          <div className="login-form">
            {!verificationSent ? (
              <div className="login-submit">
                <Button variant="primary" disabled={isLoading} onClick={handleSendVerification}>
                  {isLoading ? 'Sending...' : 'Send Verification Email'}
                </Button>
              </div>
            ) : (
              <>
                <label>Verification Code</label>
                <input
                  className="login-input"
                  placeholder="Enter verification code"
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, handleConfirmVerification)}
                  autoFocus
                />
                <div className="login-submit">
                  <Button variant="primary" disabled={!verifyCode || isLoading} onClick={handleConfirmVerification}>
                    {isLoading ? 'Verifying...' : 'Verify Email'}
                  </Button>
                </div>
              </>
            )}
          </div>
          <div className="forgot-link">
            <Button variant="text" disabled={isLoading} onClick={cancel}>Return</Button>
          </div>
        </div>
      )}
    </div>
  )
}
